/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { GuestReturnLabel } from '@/lib/order/guest-lookup.server';

export interface ReturnLabelPanelProps {
    label: GuestReturnLabel;
}

/**
 * Decode a base64 string into a Blob object URL for download. Runs on the client only (uses
 * `atob`/`Blob`/`URL`), returning `null` when the input is empty or malformed so the caller can
 * fall back to the "label unavailable" message.
 */
function useLabelDownloadUrl(base64: string | null): string | null {
    const [url, setUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!base64) {
            setUrl(null);
            return;
        }
        try {
            const binary = globalThis.atob(base64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }
            const objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
            setUrl(objectUrl);
            return () => URL.revokeObjectURL(objectUrl);
        } catch {
            setUrl(null);
        }
    }, [base64]);
    return url;
}

/**
 * Persistent panel shown after a return that produced a physical DHL return label. The cartridge
 * returns the label PDF inline (base64) in the return response — unlike SFRA's ReturnSuccess page,
 * there is no session to read it back from — so this decodes it to a Blob object URL and offers a
 * download. When the label degraded (no PDF data), it shows a fallback message instead. The panel
 * lives in the Track Order view (not the dialog) so it survives the dialog closing.
 */
export function ReturnLabelPanel({ label }: ReturnLabelPanelProps): ReactElement {
    const { t } = useTranslation('trackOrder');
    const downloadUrl = useLabelDownloadUrl(label.labelDataBase64);

    return (
        <Card data-testid="return-label-panel" className="border-primary">
            <CardHeader>
                <CardTitle className="text-base">{t('return.label.title')}</CardTitle>
                <CardDescription>{t('return.label.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                {label.trackingId && (
                    <p className="text-sm text-muted-foreground" data-testid="return-label-tracking">
                        {t('return.label.trackingId', { id: label.trackingId })}
                    </p>
                )}
                {downloadUrl ? (
                    <Button asChild size="sm" className="w-full sm:w-auto">
                        <a href={downloadUrl} download={`return-label-${label.dhlPackageId}.pdf`}>
                            {t('return.label.download')}
                        </a>
                    </Button>
                ) : (
                    <p className="text-sm text-muted-foreground" data-testid="return-label-unavailable">
                        {t('return.label.unavailable')}
                    </p>
                )}
            </CardContent>
        </Card>
    );
}

export default ReturnLabelPanel;
